/**
 * Places Wave B Charter extras: Site Rank fallback + AI Mandate.
 * Soft≠Live · Preferred crew ≠ Machine Group · Mandate ≠ Rank ≠ Ops Focus.
 */
import { useEffect, useMemo, useState } from "react";
import { html, Ico, Field, Input, CraftSelect } from "./ui.js";
import { api, useApi, useAction } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { placesPreferredCrewOptionsOf } from "./places-gaps.js";
import { personnelRosterOf } from "./response-live.js";

const MODES = [
  { value: "inside_first", label: "Inside first", hint: "Prefer people inside rings" },
  { value: "nearest", label: "Nearest", hint: "Distance only" },
  { value: "manager_first", label: "Manager first", hint: "Named Site manager floats" },
];

const WATCH_KINDS = [
  { value: "site", label: "This work (Site)" },
  { value: "zone", label: "Zone id" },
  { value: "group", label: "Machine Group label" },
];

export function PlacesCharterWaveB({ siteId, toast, personnel: personnelProp }) {
  const site = String(siteId || "").trim();
  const policyPath = site ? `/resource-fabric/sites/${encodeURIComponent(site)}/dispatch-policy` : "";
  const watchPath = site ? `/resource-fabric/sites/${encodeURIComponent(site)}/ai-watches` : "";
  const peopleApi = useApi(personnelProp ? "" : "/personnel?limit=200");
  const policyApi = useApi(policyPath);
  const watchApi = useApi(watchPath);
  const savePolicy = useAction();
  const saveWatch = useAction();

  const personnel = useMemo(() => {
    if (Array.isArray(personnelProp) && personnelProp.length) return personnelProp;
    return personnelRosterOf(peopleApi.data);
  }, [personnelProp, peopleApi.data]);

  const policyOk = !policyApi.error && policyApi.data && policyApi.data.ok !== false;
  const watchOk = !watchApi.error && watchApi.data && watchApi.data.ok !== false;
  const seatLive = policyOk || watchOk;
  const seatBlocked = !!(policyApi.error || watchApi.error);

  const [manager, setManager] = useState("");
  const [crew, setCrew] = useState("");
  const [mode, setMode] = useState("inside_first");
  const [enabled, setEnabled] = useState(false);
  const [dirty, setDirty] = useState(false);

  const [wKind, setWKind] = useState("zone");
  const [wId, setWId] = useState("");
  const [wLabel, setWLabel] = useState("");

  useEffect(() => {
    if (dirty || !policyApi.data) return;
    const p = policyApi.data.policy || {};
    setManager(String(p.manager_username || ""));
    setCrew(String(p.preferred_crew || p.preferred_group || ""));
    setMode(String(p.ai_priority_mode || "inside_first"));
    setEnabled(p.enabled === true);
  }, [policyApi.data, dirty]);

  const crewOpts = useMemo(
    () => placesPreferredCrewOptionsOf(personnel, crew),
    [personnel, crew],
  );

  const watches = (watchApi.data && watchApi.data.items) || [];

  const onSaveFallback = async () => {
    if (seatBlocked && !policyOk) {
      toast && toast("Site fallback API unavailable — Soft≠Live", "warn");
      return;
    }
    try {
      await savePolicy.run(() =>
        api(policyPath, {
          method: "PUT",
          body: {
            manager_username: manager.trim(),
            preferred_crew: crew.trim(),
            preferred_group: crew.trim(),
            ai_priority_mode: mode,
            enabled: enabled === true,
          },
        }),
      );
      setDirty(false);
      policyApi.reload && policyApi.reload();
      toast && toast("Site fallback Rank saved", "go");
    } catch (e) {
      toast && toast(String((e && e.message) || e || "save failed"), "bad");
    }
  };

  const onAddWatch = async () => {
    if (seatBlocked && !watchOk) {
      toast && toast("Mandate API unavailable — Soft≠Live", "warn");
      return;
    }
    const oid = wKind === "site" ? site : wId.trim();
    if (!oid) {
      toast && toast("Mandate needs an object id", "warn");
      return;
    }
    try {
      await saveWatch.run(() =>
        api(watchPath, {
          method: "PUT",
          body: {
            object_kind: wKind,
            object_id: oid,
            label: wLabel.trim(),
            enabled: true,
          },
        }),
      );
      setWId("");
      setWLabel("");
      watchApi.reload && watchApi.reload();
      toast && toast("Mandate saved", "go");
    } catch (e) {
      toast && toast(String((e && e.message) || e || "mandate failed"), "bad");
    }
  };

  const onRemoveWatch = async (kind, oid) => {
    try {
      await api(
        `${watchPath}?object_kind=${encodeURIComponent(kind)}&object_id=${encodeURIComponent(oid)}`,
        { method: "DELETE" },
      );
      watchApi.reload && watchApi.reload();
      toast && toast("Mandate removed", "go");
    } catch (e) {
      toast && toast(String((e && e.message) || e || "remove failed"), "bad");
    }
  };

  if (!site) {
    return html`<section className="places-wave-b" data-cmc-page="places-wave-b"
      data-cmc-charter-site="" data-seat="closed">
      <p className="note">No site yet. Charter stays closed until a work is selected.</p>
    </section>`;
  }

  return html`<section className="places-wave-b" data-cmc-page="places-wave-b"
    data-cmc-charter-site=${site}
    data-seat=${seatLive ? "live" : (seatBlocked ? "blocked" : "loading")}>
    ${(savePolicy.envelope || saveWatch.envelope)
      ? html`<p className="note" data-cmc-last-action="1">Last action · ${envelopeSummary(savePolicy.envelope || saveWatch.envelope) || "—"}</p>`
      : null}
    <header className="places-wave-b__head">
      <p className="eyebrow">${seatLive ? "Wave B · seat open" : (seatBlocked ? "Wave B · API sealed" : "Wave B · loading")}</p>
      <h3>When the pin misses every ring</h3>
      <p className="note">Site fallback Rank · Preferred crew is people, not Machine Group ink. Mandate is AI attention only${!seatLive && seatBlocked ? " — routes not mounted (Soft≠Live)." : "."}</p>
    </header>

    ${seatBlocked && !seatLive ? html`<p className="note places-wave-b__seal">
      RESOURCE_FABRIC_ADMIN_HTTP may be off, or Site Authority sealed this write. Soft≠Live — no pretend save.
    </p>` : null}

    <div className="places-wave-b__card">
      <div className="places-wave-b__card-head">
        <b>Site fallback Rank</b>
        <label className="places-wave-b__tog">
          <input type="checkbox" checked=${enabled}
            disabled=${seatBlocked && !policyOk}
            onChange=${(e) => { setEnabled(e.target.checked); setDirty(true); }}/>
          Enabled
        </label>
      </div>
      <div className="places-wave-b__grid">
        <${Field} label="Site manager">
          <${Input} value=${manager}
            onChange=${(e) => { setManager(e.target.value); setDirty(true); }}
            placeholder="roster username"/>
        <//>
        <${Field} label="Preferred crew">
          <${Input} value=${crew}
            onChange=${(e) => { setCrew(e.target.value); setDirty(true); }}
            placeholder="Roster crew tag (not Machine Group)"/>
          ${crewOpts.length ? html`<${CraftSelect} aria-label="Preferred crew pick" value=${crew} allowEmpty
            placeholder="Pick from Roster…"
            options=${crewOpts}
            onChange=${(e) => { setCrew(e.target.value); setDirty(true); }}/>` : null}
        <//>
        <${Field} label="Rank mode">
          <${CraftSelect} aria-label="Rank mode" value=${mode}
            options=${MODES}
            onChange=${(e) => { setMode(e.target.value || "inside_first"); setDirty(true); }}/>
        <//>
      </div>
      <button type="button" className="btn btn--sm"
        disabled=${!dirty || savePolicy.busy || (seatBlocked && !policyOk)}
        onClick=${onSaveFallback}>
        <${Ico} d="check" w=${13}/> Save fallback
      </button>
    </div>

    <div className="places-wave-b__card">
      <div className="places-wave-b__card-head">
        <b>AI Mandate</b>
        <em>≠ Rank · ≠ Ops Focus</em>
      </div>
      <div className="places-wave-b__grid">
        <${Field} label="Hang on">
          <${CraftSelect} aria-label="Mandate kind" value=${wKind}
            options=${WATCH_KINDS}
            onChange=${(e) => setWKind(e.target.value || "zone")}/>
        <//>
        ${wKind !== "site" ? html`<${Field} label="Object id">
          <${Input} value=${wId} onChange=${(e) => setWId(e.target.value)}
            placeholder=${wKind === "group" ? "Night" : "zone space_id"}/>
        <//>` : null}
        <${Field} label="Label">
          <${Input} value=${wLabel} onChange=${(e) => setWLabel(e.target.value)}
            placeholder="optional"/>
        <//>
      </div>
      <button type="button" className="btn btn--sm"
        disabled=${saveWatch.busy || (seatBlocked && !watchOk)}
        onClick=${onAddWatch}>
        <${Ico} d="spark" w=${13}/> Add Mandate
      </button>
      <ul className="places-wave-b__list">
        ${!watches.length
          ? html`<li className="note">No Mandates on this work yet.</li>`
          : watches.map((w) => html`<li key=${w.object_kind + ":" + w.object_id}>
              <span>${w.object_kind} · <code>${w.object_id}</code>
                ${w.label ? " · " + w.label : ""}</span>
              <button type="button" className="btn btn--ghost btn--sm"
                onClick=${() => onRemoveWatch(w.object_kind, w.object_id)}>Remove</button>
            </li>`)}
      </ul>
    </div>
  </section>`;
}

export default PlacesCharterWaveB;
